import { Injectable } from '@angular/core';
import { ModifiableNamedIdentityWithIdentifierAndCreatorDTO } from '../api/api';
import { TranslateService } from '@ngx-translate/core';
import { SbDateTime, SbLocalDateTime } from '../api/api-date-time';
import { DateTime, Duration } from 'luxon';
import { UntypedFormGroup, ValidatorFn } from '@angular/forms';

@Injectable({ providedIn: 'root' })
export class DateTransformationService {
  dateFormat: string = 'DE';

  WEEKDAYS = {
    Montag: 1,
    Dienstag: 2,
    Mittwoch: 3,
    Donnerstag: 4,
    Freitag: 5,
    Samstag: 6,
    Sonntag: 7,
    Monday: 1,
    Tuesday: 2,
    Wednesday: 3,
    Thursday: 4,
    Friday: 5,
    Saturday: 6,
    Sunday: 7,
  };
  WEEKDAYSArrayGerman = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

  constructor(private readonly translate: TranslateService) {}

  public modifiedCheck<T extends ModifiableNamedIdentityWithIdentifierAndCreatorDTO>(dto: T): DateTime | null {
    if (!dto) {
      return null;
    }
    return this.laterDate(dto.createdAt, dto.modifiedAt);
  }

  public laterDate(firstDate: SbDateTime | null, secondDate: SbDateTime | null): DateTime | null {
    return firstDate && secondDate ? DateTime.max(firstDate.value, secondDate.value) : (firstDate?.value ?? secondDate?.value ?? null);
  }

  public earlierDate(firstDate: SbDateTime | null, secondDate: SbDateTime | null): DateTime | null {
    return firstDate && secondDate ? DateTime.min(firstDate.value, secondDate.value) : (firstDate?.value ?? secondDate?.value ?? null);
  }

  public passedTime(date: SbDateTime, until?: string): string {
    if (!date) {
      return null;
    }

    const current = DateTime.local();
    const seconds = current.diff(date.value).as('milliseconds');

    // Define thresholds in milliseconds
    const thresholds: Record<string, number> = {
      minute: 45000,
      halfHour: 1800000,
      hour: 3600000,
      halfDay: 43200000,
      day: 86400000,
      week: 604800000,
      month: 2592000000,
    };

    // Determine the threshold, defaulting to 'week'
    const threshold = thresholds[until || 'week'] || thresholds['week'];

    return seconds > threshold ? date.value.toFormat('dd.MM.yyyy HH:mm') : current.diff(date.value).rescale().toHuman();
  }

  public calculateTimespanBetweenDates(from: SbDateTime, till: SbDateTime): string {
    if (!from?.value || !till?.value) {
      return this.translate.instant('lang.globals.noDateSelected');
    }
    const durationInMS = till.value.diff(from.value).toObject().milliseconds;
    if (durationInMS < 1000) {
      return '1s';
    }
    return Duration.fromMillis(durationInMS).rescale().toHuman({ unitDisplay: 'short' });
  }

  public parseTimeToDuration(time: number): string {
    if (time == null) {
      return 'missing time';
    }
    const duration = Duration.fromObject({ seconds: time });
    // rescale for transformation to days, minutes, ... (alternative use return duration.toFormat("d'd' h'h' m'm' s's'"); )
    return duration.rescale().toHuman({ unitDisplay: 'short' });
  }

  public doubleDigitDisplay(num: number): string {
    return isNaN(num) ? '00' : num.toString().padStart(2, '0');
  }

  endDateAfterStartDate: ValidatorFn = (fg: UntypedFormGroup) => {
    const start = fg.get('From').value;
    const end = fg.get('Till').value;

    if (!start || !end) {
      return null;
    }

    if (end >= start) {
      return null;
    }

    return { endDateAfterStartDate: 'true' };
  };

  getFromDate(dateCount: number) {
    const d = new Date();
    d.setDate(d.getDate() - dateCount);
    return d;
  }

  dateToDateString(dateTime: DateTime) {
    if (dateTime?.isValid) {
      switch (this.dateFormat) {
        case 'DE':
          return dateTime.toFormat('dd.MM.yyyy');
        case 'US':
          return dateTime.toFormat('MM/dd/yyyy');
        case 'GB':
          return dateTime.toFormat('dd/MM/yyyy');
        case 'ISO':
          return dateTime.toFormat('yyyy-MM-dd');
        default:
          return dateTime.toFormat('yyyy-MM-dd');
      }
    }

    return '';
  }

  dateToDateTimeString(dateTime: DateTime, showSeconds?: boolean) {
    if (!dateTime?.isValid) {
      return '';
    }
    switch (this.dateFormat) {
      case 'DE':
        // hotfix to show "00:00" instead of "24:00"
        return (showSeconds ? dateTime.toFormat('dd.MM.yyyy TT') : dateTime.toFormat('dd.MM.yyyy T')).replace(' 24:', ' 00:');
      case 'US':
        return showSeconds ? dateTime.toFormat('MM/dd/yyyy tt') : dateTime.toFormat('MM/dd/yyyy t');
      case 'GB':
        return showSeconds ? dateTime.toFormat('dd/MM/yyyy tt') : dateTime.toFormat('dd/MM/yyyy t');
      case 'ISO':
        return showSeconds ? dateTime.toFormat('yyyy-MM-dd TT') : dateTime.toFormat('yyyy-MM-dd T');
      default:
        return showSeconds ? dateTime.toFormat('yyyy-MM-dd TT') : dateTime.toFormat('yyyy-MM-dd T');
    }
  }

  parseDateFromDateString(date: string) {
    // Try to parse a DateTime from a String
    if (!date || date === '' || !date?.trim()) {
      return null;
    }

    // Try ISO format first
    let dateTime = DateTime.fromISO(date);
    if (dateTime.isValid) {
      return dateTime;
    }

    // List of known formats to try
    const formats = [
      'dd.MM.yyyy',
      'dd.MM.yy',
      'yyyy-MM-dd',
      'MM/dd/yyyy',
      'd.MM.yy',
      'dd.M.yy',
      'd.M.yy',
      'd.MM.yy HH:mm',
      'dd.M.yy HH:mm',
      'd.M.yy HH:mm',
      'd.MM.yy H:mm',
      'dd.M.yy H:mm',
      'd.M.yy H:mm',
      'dd.MM.yy HH:mm',
      'dd.MM.yyyy HH:mm',
      'dd.MM.yy HH:mm:ss',
      'dd.MM.yyyy HH:mm:ss',
    ];

    for (const format of formats) {
      dateTime = DateTime.fromFormat(date, format);
      if (dateTime.isValid) {
        return dateTime;
      }
    }

    return null;
  }

  getTimeframePercentage(startDate: any, endDate: any) {
    if (!startDate || !endDate) {
      return 0;
    }
    const completed = Math.round(this.getTimeframeValue(startDate, endDate) * 100);
    if (completed > 100) {
      return 100;
    }
    if (completed < 0) {
      return 0;
    }
    return completed;
  }

  getTimeframeValue(startDate: any, endDate: any) {
    if (!startDate || !endDate) {
      return 0;
    }
    const start = new Date(startDate) as any;
    const end = new Date(endDate) as any;
    const today = new Date() as any;
    return (today - start) / (end - start);
  }

  localDateToJSDate(localDate: SbLocalDateTime | SbDateTime): Date {
    const dateConvert = {
      year: localDate.value.year,
      month: localDate.value.month - 1,
      day: localDate.value.day,
      hour: localDate.value.hour,
      minute: localDate.value.minute,
      second: localDate.value.second,
      millisecond: localDate.value.millisecond,
    };
    return new Date(
      dateConvert.year,
      dateConvert.month,
      dateConvert.day,
      dateConvert.hour,
      dateConvert.minute,
      dateConvert.second,
      dateConvert.millisecond
    );
  }

  parseDurationToSeconds(pDuration: Duration): number {
    let resultSeconds: number = 0;
    if (!pDuration) {
      return 0;
    }
    if (pDuration.years) {
      resultSeconds += pDuration.years * 31536000;
    }
    if (pDuration.months) {
      resultSeconds += pDuration.months * 2592000;
    }
    if (pDuration.days) {
      resultSeconds += pDuration.days * 86400;
    }
    if (pDuration.hours) {
      resultSeconds += pDuration.hours * 3600;
    }
    if (pDuration.minutes) {
      resultSeconds += pDuration.minutes * 60;
    }
    if (pDuration.seconds) {
      resultSeconds += pDuration.seconds;
    }
    return resultSeconds;
  }

  getHourTimeSlotString(index: number): string {
    if (index === null || index === undefined) {
      return 'Missing index';
    }
    if (index > 23) {
      return 'Index too large';
    }
    return this.doubleDigitDisplay(index) + ':00-' + this.doubleDigitDisplay(index) + ':59';
  }

  getDayTranslation(day: number, length?: number): string {
    let dayTranslation: string;
    switch (day) {
      case 1:
        dayTranslation = this.translate.instant('lang.globals.weekday.monday');
        break;
      case 2:
        dayTranslation = this.translate.instant('lang.globals.weekday.tuesday');
        break;
      case 3:
        dayTranslation = this.translate.instant('lang.globals.weekday.wednesday');
        break;
      case 4:
        dayTranslation = this.translate.instant('lang.globals.weekday.thursday');
        break;
      case 5:
        dayTranslation = this.translate.instant('lang.globals.weekday.friday');
        break;
      case 6:
        dayTranslation = this.translate.instant('lang.globals.weekday.saturday');
        break;
      case 7:
        dayTranslation = this.translate.instant('lang.globals.weekday.sunday');
        break;
      default:
        dayTranslation = this.translate.instant('lang.globals.unknown');
        break;
    }
    if (length && length > 0) {
      dayTranslation = dayTranslation.substring(0, length);
    }
    return dayTranslation;
  }

  getMonthTranslation(month: number, length?: number): string {
    let monthTranslation: string;
    switch (month) {
      case 1:
        monthTranslation = this.translate.instant('lang.globals.month.january');
        break;
      case 2:
        monthTranslation = this.translate.instant('lang.globals.month.february');
        break;
      case 3:
        monthTranslation = this.translate.instant('lang.globals.month.march');
        break;
      case 4:
        monthTranslation = this.translate.instant('lang.globals.month.april');
        break;
      case 5:
        monthTranslation = this.translate.instant('lang.globals.month.may');
        break;
      case 6:
        monthTranslation = this.translate.instant('lang.globals.month.june');
        break;
      case 7:
        monthTranslation = this.translate.instant('lang.globals.month.july');
        break;
      case 8:
        monthTranslation = this.translate.instant('lang.globals.month.august');
        break;
      case 9:
        monthTranslation = this.translate.instant('lang.globals.month.september');
        break;
      case 10:
        monthTranslation = this.translate.instant('lang.globals.month.october');
        break;
      case 11:
        monthTranslation = this.translate.instant('lang.globals.month.november');
        break;
      case 12:
        monthTranslation = this.translate.instant('lang.globals.month.december');
        break;
      default:
        monthTranslation = this.translate.instant('lang.globals.unknown');
        break;
    }
    if (length && length > 0) {
      monthTranslation = monthTranslation.substring(0, length);
    }
    return monthTranslation;
  }
}
